import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Pause, Play } from 'lucide-react';
import { useLang } from '../i18n';

/**
 * Un loop video che non costa nulla finche' non serve.
 *
 * Stesso patto dell'hero e di ShowroomReel: `preload="none"` e nessuna
 * sorgente al primo render; il file arriva quando il riquadro e' a 200px dalla
 * vista, in 720p sotto i 768px, e mai con «risparmio dati» o con reduced
 * motion — li' resta il poster. La sorgente si sceglie una volta sola: cambiarla
 * a ogni resize riscaricherebbe il video.
 *
 * `attivo` ferma il loop senza scaricarlo di nuovo (capitoli di /azienda:
 * uno alla volta); `carica` decide se e' il momento di scaricarlo (il
 * capitolo attivo e il successivo). Il pulsante pausa c'e' anche sui loop
 * decorativi: WCAG 2.2.2 vale per tutto cio' che si muove per piu' di 5 s.
 *
 * Senza `src` resta il poster con un Ken Burns CSS, spento con reduced motion.
 */
export const LoopVideo = ({
  src,
  srcMobile,
  poster,
  width = 1920,
  height = 1080,
  label,
  priority = false,
  carica = true,
  attivo = true,
  className = '',
  testid,
  pausaClassName = 'right-4 top-4',
  // classi in piu' su poster e video (non sul pulsante): l'hero le usa per lo
  // zoom legato allo scroll senza trascinarsi dietro il pulsante pausa
  mediaClassName = '',
}) => {
  const { t } = useLang();
  const ridotto = useReducedMotion();
  const box = useRef(null);
  const video = useRef(null);
  const [visibile, setVisibile] = useState(false);
  const [sorgente, setSorgente] = useState(null);
  const [fermato, setFermato] = useState(false);
  const [inRiproduzione, setInRiproduzione] = useState(false);

  useEffect(() => {
    if (!src || !box.current) return undefined;
    const io = new IntersectionObserver(([e]) => setVisibile(e.isIntersecting), { rootMargin: '200px' });
    io.observe(box.current);
    return () => io.disconnect();
  }, [src]);

  useEffect(() => {
    if (sorgente || !src || !visibile || !carica || ridotto) return;
    if (navigator.connection?.saveData) return;
    const piccolo = window.matchMedia?.('(max-width: 767px)').matches;
    setSorgente(piccolo && srcMobile ? srcMobile : src);
  }, [sorgente, src, srcMobile, visibile, carica, ridotto]);

  useEffect(() => {
    const v = video.current;
    if (!v || !sorgente) return;
    if (attivo && visibile && !ridotto && !fermato) v.play?.()?.catch?.(() => {});
    else v.pause?.();
  }, [sorgente, attivo, visibile, ridotto, fermato]);

  if (!src && !poster) return null;

  const decorativo = label ? undefined : true;

  if (!src) {
    return (
      <div ref={box} data-testid={testid} className={`overflow-hidden ${className}`}>
        <img
          src={poster}
          alt={label || ''}
          aria-hidden={decorativo}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
          decoding="async"
          style={fermato ? { animationPlayState: 'paused' } : undefined}
          className={`kenburns absolute inset-0 h-full w-full object-cover ${mediaClassName}`}
        />
        {!ridotto && (
          <button
            type="button"
            onClick={() => setFermato((f) => !f)}
            aria-label={t(fermato ? 'common.playVideo' : 'common.pauseVideo')}
            className={`absolute z-10 grid h-10 w-10 place-items-center rounded-full bg-inchiostro/70 text-carta transition-colors hover:bg-inchiostro/90 ${pausaClassName}`}
          >
            {fermato ? <Play size={16} strokeWidth={2} /> : <Pause size={16} strokeWidth={2} />}
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={box} data-testid={testid} className={`overflow-hidden ${className}`}>
      {priority && (
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          width={width}
          height={height}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className={`absolute inset-0 h-full w-full object-cover ${mediaClassName}`}
        />
      )}
      <video
        ref={video}
        src={sorgente || undefined}
        poster={poster}
        width={width}
        height={height}
        loop
        muted
        playsInline
        preload="none"
        aria-hidden={decorativo}
        aria-label={label || undefined}
        onPlay={() => setInRiproduzione(true)}
        onPause={() => setInRiproduzione(false)}
        className={`absolute inset-0 h-full w-full object-cover ${mediaClassName}`}
      />
      {sorgente && attivo && (
        <button
          type="button"
          onClick={() => {
            if (inRiproduzione) {
              setFermato(true);
            } else {
              setFermato(false);
              video.current.play?.()?.catch?.(() => {});
            }
          }}
          aria-label={t(inRiproduzione ? 'common.pauseVideo' : 'common.playVideo')}
          className={`absolute z-10 grid h-10 w-10 place-items-center rounded-full bg-inchiostro/70 text-carta transition-colors hover:bg-inchiostro/90 ${pausaClassName}`}
        >
          {inRiproduzione ? <Pause size={16} strokeWidth={2} /> : <Play size={16} strokeWidth={2} />}
        </button>
      )}
    </div>
  );
};
